import React from 'react';
import { createPortal } from 'react-dom';

/**
 * Read-only popup showing full putaway details including bin assignments.
 * No editing, no Save — Close only.
 */
const num = (v) => (v != null && Number.isFinite(Number(v)) ? Number(v) : '—');

const Section = ({ title, children }) => (
  <div className="mb-6">
    <h3 className="text-sm font-semibold text-gray-700 mb-2 border-b border-gray-100 pb-1">{title}</h3>
    <div className="text-sm text-gray-900">{children}</div>
  </div>
);

const ViewPutawayPopup = ({
  open,
  onClose,
  loading,
  error,
  data,
  onRetry,
}) => {
  if (!open) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  const product = data?.product ?? data?.productId;
  const productSku = product && typeof product === 'object' ? (product.sku ?? product.skuName) : (data?.productId ?? '—');
  const productDesc = product && typeof product === 'object' ? (product.description ?? '') : '';
  const assignments = data?.assignments ?? data?.binAssignments ?? [];

  const popupContent = (
    <div
      className="fixed inset-0 flex items-center justify-center p-3 md:p-4"
      style={{ zIndex: 99999 }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="putaway-details-title"
    >
      <div
        className="absolute inset-0 bg-black/20"
        onClick={handleBackdropClick}
        aria-hidden="true"
      />
      <div
        className="relative w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col bg-white rounded-[18px] border border-[#EEF1F5] shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between shrink-0 px-6 pt-6 pb-2">
          <h2 id="putaway-details-title" className="text-lg font-semibold text-[#101828]">
            {loading ? 'Putaway Details' : `Putaway Details — ${data?.putawayId ?? data?._id ?? '—'}`}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="w-7 h-7 rounded-full border border-[#D0D5DD] text-[#667085] flex items-center justify-center bg-white hover:bg-[#F6F7FA] transition"
            aria-label="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <hr className="border-0 border-t border-[#EEF1F5] mt-2" />
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading && (
            <div className="py-8 text-center text-gray-500 text-sm">
              Loading...
            </div>
          )}
          {error && !loading && (
            <div className="py-8 text-center">
              <p className="text-red-600 text-sm mb-3">{error}</p>
              {onRetry && (
                <button
                  type="button"
                  onClick={onRetry}
                  className="rounded-xl border border-[#188742] bg-[#188742]/10 px-4 py-2 text-sm font-medium text-[#188742] hover:bg-[#188742]/20"
                >
                  Retry
                </button>
              )}
            </div>
          )}
          {!loading && !error && data && (
            <>
              <Section title="Putaway details">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
                  <div><span className="text-gray-500">Putaway ID:</span> {data.putawayId ?? '—'}</div>
                  <div><span className="text-gray-500">Transaction:</span> {data.transactionId ?? '—'}</div>
                  <div className="col-span-2"><span className="text-gray-500">Product:</span> {productSku}{productDesc ? ` — ${productDesc}` : ''}</div>
                  <div><span className="text-gray-500">Status:</span> {data.status ?? '—'}</div>
                </div>
              </Section>

              <Section title="Quantity summary">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2">
                  <div><span className="text-gray-500">Invoice Qty:</span> {num(data.invoiceQuantity)}</div>
                  <div><span className="text-gray-500">Received Qty:</span> {num(data.receivedQuantity)}</div>
                  <div><span className="text-gray-500">Assigned Qty:</span> {num(data.assignedQuantity)}</div>
                  <div><span className="text-gray-500">Remaining Qty:</span> {num(data.remainingQuantity)}</div>
                  <div><span className="text-gray-500">Qty Per Bin:</span> {num(data.qtyPerBin)}</div>
                  <div><span className="text-gray-500">Suggested Bins:</span> {num(data.suggestedBins)}</div>
                </div>
              </Section>

              <Section title="Bin assignments">
                <div className="overflow-x-auto rounded-xl border border-[#EEF1F5]">
                  <table className="w-full border-collapse text-sm">
                    <thead>
                      <tr className="bg-[#F6F7FA]">
                        <th className="text-left font-semibold text-[#98A2B3] px-3 py-2 rounded-tl-xl">Bin Code</th>
                        <th className="text-left font-semibold text-[#98A2B3] px-3 py-2">Bin Type</th>
                        <th className="text-left font-semibold text-[#98A2B3] px-3 py-2">Condition</th>
                        <th className="text-right font-semibold text-[#98A2B3] px-3 py-2">Assigned Qty</th>
                        <th className="text-right font-semibold text-[#98A2B3] px-3 py-2 rounded-tr-xl">Current Qty (bin)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {assignments.length > 0 ? assignments.map((row, i) => {
                        const bin = row.binId && typeof row.binId === 'object' ? row.binId : null;
                        const binCode = bin?.binCode ?? row.binCode ?? '—';
                        const binType = bin?.binType ?? '—';
                        const currentQty = bin?.currentQuantity != null ? bin.currentQuantity : '—';
                        const qty = row.assignedQty ?? row.assignedQuantity ?? '—';
                        const condition = row.condition ?? '—';
                        return (
                          <tr key={i} className="border-t border-[#EEF1F5]">
                            <td className="px-3 py-2 text-[#101828]">{binCode}</td>
                            <td className="px-3 py-2 text-[#101828]">{binType}</td>
                            <td className="px-3 py-2 text-[#101828]">{condition}</td>
                            <td className="px-3 py-2 text-right text-[#101828]">{qty}</td>
                            <td className="px-3 py-2 text-right text-[#101828]">{currentQty}</td>
                          </tr>
                        );
                      }) : (
                        <tr>
                          <td colSpan={5} className="px-3 py-6 text-center text-gray-500">
                            No bins assigned
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </Section>
            </>
          )}
        </div>
        {!loading && (
          <div className="shrink-0 px-6 py-4 border-t border-[#EEF1F5] flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-[#D0D5DD] bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(popupContent, document.body);
};

export default ViewPutawayPopup;
